import {useMemo, useRef} from 'react';

import {
    MarketingSplitCardsCarouselV1Query,
    useMarketingSplitCardsCarouselV1Query,
} from 'src/generated/graphql';

type SplitCardsCarousel = NonNullable<
    MarketingSplitCardsCarouselV1Query['marketingSplitCardsCarouselV1']
>;

/**
 * The CMS `headline` field may arrive wrapped in an HTML tag (e.g. `<p>…</p>`
 * or `<h3>…</h3>`) and may include trailing empty paragraphs. The consuming
 * component already renders the value inside its own heading element via
 * `dangerouslySetInnerHTML`, so the wrapping tags must be removed to avoid
 * invalid nested markup that breaks SSR rendering.
 */
export const stripWrappingHeadingTags = (html: string): string =>
    html
        .replace(/^<(h[1-6]|p)[^>]*>([\s\S]*?)<\/\1>/, '$2')
        .replace(/\s*<p>\s*<\/p>\s*$/, '')
        .trim();

export const useMarketingSplitCardsData = () => {
    const {data, isLoading, error} = useMarketingSplitCardsCarouselV1Query();

    // The client refetch sends `ud_locale`, which the CMS resolves to null. Keep the last
    // populated response so that null can't blank the server-rendered module.
    const lastPopulated = useRef<SplitCardsCarousel | undefined>();

    return useMemo(() => {
        const response = data?.marketingSplitCardsCarouselV1;

        if (response?.cards?.length) {
            lastPopulated.current = response;
        }

        const graphqlData = response?.cards?.length ? response : lastPopulated.current;
        const hasGraphqlData = graphqlData && graphqlData.cards && graphqlData.cards.length > 0;

        let header: {headline?: string; supportingText?: string | null} | null;
        let cards: Array<{title: string; url: string; image: {url: string}}>;

        if (hasGraphqlData) {
            const rawHeadline = graphqlData.header?.headline;
            header = {
                ...graphqlData.header,
                headline: rawHeadline ? stripWrappingHeadingTags(String(rawHeadline)) : rawHeadline,
            };
            cards = graphqlData.cards
                .filter((card): card is NonNullable<typeof card> => Boolean(card))
                .map((card) => ({
                    title: card.title,
                    url: card.url,
                    image: {url: card.image?.url || ''},
                }));
        } else {
            // No data available
            header = null;
            cards = [];
        }

        return {
            data: graphqlData,
            isLoading,
            error,
            header,
            cards,
            hasData: hasGraphqlData,
        };
    }, [data, isLoading, error]);
};
